import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { Event } from 'react-big-calendar';

import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';
import { getDay } from 'app/utils/dayjs';

import type { AvailabilityFormValues } from '../_components/AvailabilityForm/availabilityForm.types';
import type { TimeslotEvent } from '../_components/CalendarEvent/CalendarEvent';

import { getIsoWeekday } from './weekdays';

export const getPopoverPlacement = (date?: Date): 'left' | 'right' => {
  if (!date) {
    return 'right';
  }

  const day = getDay(date);

  return day === 0 || day >= 4 ? 'left' : 'right';
};

export const getEventKey = (event: Event): string =>
  (event.resource as { id?: string } | undefined)?.id ?? '';

const popoverKeys = new WeakMap<object, string>();
let popoverKeyCounter = 0;

export const getPopoverKey = (event: Event): string => {
  const target = (event.resource as object | undefined) ?? event;
  const existing = popoverKeys.get(target);

  if (existing !== undefined) {
    return existing;
  }

  const key = `popover-${(popoverKeyCounter += 1)}`;

  popoverKeys.set(target, key);

  return key;
};

const combineDateTime = (date: Dayjs, time: Dayjs): Date =>
  date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .toDate();

export const adjustEndForMidnight = (start: Date, end: Date): Date =>
  end.getTime() > start.getTime() ? end : dayjs(end).add(1, 'day').toDate();

export type IsDayClosed = (date: Dayjs) => boolean;

let createdEventCounter = 0;
let createdSeriesCounter = 0;

const buildEventOn = (
  date: Dayjs,
  values: AvailabilityFormValues,
  id: string,
  isTemporary: boolean,
  isClosed: boolean,
  seriesId?: string
): TimeslotEvent => {
  const start = combineDateTime(date, values.startTime);
  const end = adjustEndForMidnight(
    start,
    combineDateTime(date, values.endTime)
  );

  return {
    title: '',
    start,
    end,
    resource: {
      id,
      seriesId,
      slotsLeft: values.capacity,
      totalBooked: 0,
      hasStock: true,
      isRecurrent: values.frequency !== FREQUENCY_MODE.NONE,
      isClosed,
      isTemporary,
      frequency: values.frequency,
      weekdays: values.weekdays,
    },
  };
};

export const buildEvents = (
  values: AvailabilityFormValues,
  isDayClosed?: IsDayClosed
): TimeslotEvent[] => {
  const seriesId =
    values.frequency !== FREQUENCY_MODE.NONE
      ? `series-${(createdSeriesCounter += 1)}`
      : undefined;

  return previewDates(values).map((day) =>
    buildEventOn(
      day,
      values,
      `created-${(createdEventCounter += 1)}`,
      false,
      isDayClosed?.(day) ?? false,
      seriesId
    )
  );
};

const restOfWeek = (start: Dayjs): Dayjs[] => {
  const dates: Dayjs[] = [];

  for (let day = start; ; day = day.add(1, 'day')) {
    dates.push(day);

    if (day.day() === 0) {
      break;
    }
  }

  return dates;
};

const previewDates = (values: AvailabilityFormValues): Dayjs[] => {
  if (values.frequency === FREQUENCY_MODE.DAILY) {
    return restOfWeek(values.date);
  }

  if (values.frequency === FREQUENCY_MODE.CUSTOM) {
    return restOfWeek(values.date).filter((day) =>
      values.weekdays.includes(getIsoWeekday(day))
    );
  }

  return [values.date];
};

export const buildTemporaryEvents = (
  values: AvailabilityFormValues,
  excludeDate?: Dayjs,
  isDayClosed?: IsDayClosed
): TimeslotEvent[] =>
  previewDates(values)
    .filter((day) => !excludeDate || !day.isSame(excludeDate, 'day'))
    .map((day) =>
      buildEventOn(
        day,
        values,
        `temporary-${day.format('YYYY-MM-DD')}`,
        true,
        isDayClosed?.(day) ?? false
      )
    );

export const sameTemporaryEvents = (
  a: TimeslotEvent[],
  b: TimeslotEvent[]
): boolean =>
  a.length === b.length &&
  a.every(
    (event, index) =>
      event.start?.getTime() === b[index].start?.getTime() &&
      event.end?.getTime() === b[index].end?.getTime() &&
      event.resource.slotsLeft === b[index].resource.slotsLeft &&
      event.resource.isRecurrent === b[index].resource.isRecurrent
  );
